/**
 * TradeFinder chimes (browser only, optional, default off).
 *
 * A short two-note WebAudio chime: rising for a Buy signal, falling for a
 * Sell. Played only while the tab is visible, at the user's volume, and only
 * for signals that pass the same filters as the phone push (evaluateAlert)
 * plus the per-kind chime switch.
 */
import type { FlowSignal } from '../store/flowSignalsStore';
import type { KeyLevel } from '../utils/keyLevels';
import {
  evaluateAlert,
  newAlertState,
  type AlertKind,
  type AlertState,
  type FlowAlertSettings,
} from '../components/TradeFinder/tradeFinderUx';
import type { TradeFinderSound } from '../store/tradeFinderUxStore';

/** Note pairs, Hz: buy rises (E5 -> A5), sell falls (A4 -> E4). */
export const BUY_TONES: readonly [number, number] = [659.25, 880];
export const SELL_TONES: readonly [number, number] = [440, 329.63];
const NOTE_S = 0.12;

export function tonesFor(side: string): readonly [number, number] {
  return side === 'buy' ? BUY_TONES : SELL_TONES;
}

export interface ChimeDeps {
  visible: () => boolean;
  play: (tones: readonly [number, number], volume: number) => void;
}

/**
 * Which of `signals` to chime for (at most one per call: the first that
 * passes, so a burst is one sound). Pure apart from `state`.
 */
export function pickChime(
  signals: readonly FlowSignal[],
  sound: TradeFinderSound,
  alerts: FlowAlertSettings | null,
  levels: readonly KeyLevel[] | null,
  state: AlertState,
  now: number,
): FlowSignal | null {
  if (!sound.enabled || sound.volume <= 0 || !alerts) return null;
  for (const s of signals) {
    const r = evaluateAlert(s, alerts, levels, state, now, (k) => sound.kinds[k as AlertKind] === true);
    if (r.ok) return s;
  }
  return null;
}

/** Gate + play: returns the signal chimed for, or null. */
export function maybeChime(
  signals: readonly FlowSignal[],
  sound: TradeFinderSound,
  alerts: FlowAlertSettings | null,
  levels: readonly KeyLevel[] | null,
  state: AlertState,
  now: number,
  deps: ChimeDeps,
): FlowSignal | null {
  if (!deps.visible()) return null;
  const s = pickChime(signals, sound, alerts, levels, state, now);
  if (s) deps.play(tonesFor(s.side), sound.volume);
  return s;
}

let audioCtx: AudioContext | null = null;

/** The real player: a soft sine two-note chime. */
export function playChime(tones: readonly [number, number], volume: number): void {
  try {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    audioCtx = audioCtx ?? new Ctor();
    const ctx = audioCtx;
    if (ctx.state === 'suspended') void ctx.resume();
    const t0 = ctx.currentTime + 0.01;
    const peak = Math.max(0, Math.min(1, volume)) * 0.3;
    tones.forEach((hz, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      const start = t0 + i * NOTE_S;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + NOTE_S * 1.6);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + NOTE_S * 1.7);
    });
  } catch {
    /* no audio (autoplay policy, no device): silently skip */
  }
}

export const browserChimeDeps: ChimeDeps = {
  visible: () => typeof document !== 'undefined' && document.visibilityState === 'visible',
  play: playChime,
};

/** One cooldown / rate state per tab, and each signal chimes once however many charts show it. */
const tabState: AlertState = newAlertState();
const chimed = new Set<string>();

/** Live batch -> at most one chime (charts call this from onLiveFlowSignals). */
export function chimeForLive(
  signals: readonly FlowSignal[],
  sound: TradeFinderSound,
  alerts: FlowAlertSettings | null,
  levels: readonly KeyLevel[] | null,
  deps: ChimeDeps = browserChimeDeps,
  now = Date.now(),
): FlowSignal | null {
  const fresh = signals.filter((s) => !chimed.has(s.id));
  if (fresh.length === 0) return null;
  for (const s of fresh) chimed.add(s.id);
  if (chimed.size > 2_000) {
    // Oldest first (insertion order): keep the newest 1000.
    const drop = chimed.size - 1_000;
    let i = 0;
    for (const id of chimed) {
      if (i++ >= drop) break;
      chimed.delete(id);
    }
  }
  return maybeChime(fresh, sound, alerts, levels, tabState, now, deps);
}
